import {
	IDictionary,
	IImpersonationContext,
	IRawRichTextContentState,
	ResourceSelectorId,
	UserSessionContext,
} from '@ViewModels';
import escapeRegExp from 'lodash.escaperegexp';
import { v4 as uuid } from 'uuid';
import {
	IRichContentDocumentEditorPlaceholder,
	levPlaceholderPopoverCloseCommand,
	levPlaceholderPopoverCommand,
} from '.';
// Imported directly (not via './utils') to avoid a circular import: the utils barrel re-exports
// richContentTokenUtils, which imports this module.
import { isFilesFeatureEnabled } from './utils/accountUtils';

export enum TokenAttributes {
	DataPlaceholder = 'data-placeholder',
	DataPlaceholderValue = 'data-placeholder-value',
	DataId = 'data-id',
	ContentEditable = 'contenteditable',
}

export enum ContentEditableOptions {
	True = 'true',
	False = 'false',
}

export enum DataPlaceholderOptions {
	True = 'true',
	False = 'false',
}

export const FirstNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{firstName}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{first name}}',
	text: 'first name',
	value: 'contact.firstName',
};

export const LastNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{lastName}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{last name}}',
	text: 'last name',
	value: 'contact.lastName',
};

export const CompanyNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{companyName}}', '{{company}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{company name}}',
	text: 'company name',
	value: 'contact.companyName',
};

export const JobTitlePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{jobTitle}}'],
	symbol: '{{job title}}',
	text: 'job title',
	value: 'contact.jobTitle',
};

export const CityPlaceholder: IRichContentDocumentEditorPlaceholder = {
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{city}}',
	text: 'city',
	value: 'contact.address.city',
};

export const StatePlaceholder: IRichContentDocumentEditorPlaceholder = {
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{state}}',
	text: 'state',
	value: 'contact.address.stateProvince',
};

export const AddressPlaceholder: IRichContentDocumentEditorPlaceholder = {
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{address}}',
	text: 'address',
	value: 'contact.address.address1',
};

export const Address2Placeholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{address2}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{address 2}}',
	text: 'address 2',
	value: 'contact.address.address2',
};

export const ZipCodePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{zipcode}}', '{{zip}}', '{{postal code}}', '{{postalcode}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{zip code}}',
	text: 'zip code',
	value: 'contact.address.postalCode',
};

export const CountryPlaceholder: IRichContentDocumentEditorPlaceholder = {
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{country}}',
	text: 'country',
	value: 'contact.address.country',
};

export const FullAddressPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{fulladdress}}', '{{mailing address}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{full address}}',
	text: 'full address',
	value: 'contact.address.full',
};

export const BioPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{custom}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{bio}}',
	text: 'bio',
	value: 'contact.bio',
};

export const SenderFirstNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{sender first name}}', '{{sender firstname}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{sender}}',
	text: 'sender first name',
	value: 'sender.firstName',
};

export const SenderFullNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{sender name}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{sender full name}}',
	text: 'sender full name',
	value: 'sender.fullName',
};

export const SenderPhonePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{sender phone number}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{sender phone}}',
	text: 'sender phone number',
	value: 'sender.phoneNumber',
};

export const SenderEmailPlaceholder: IRichContentDocumentEditorPlaceholder = {
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{sender email}}',
	text: 'sender email',
	value: 'sender.email',
};

export const UnsubscribeLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{unsubscribeLink}}'],
	symbol: '{{unsubscribe link}}',
	text: 'click here',
	value: 'email.unsubscribeLink',
};

export const EmailUnsubscribePlaceholder: IRichContentDocumentEditorPlaceholder = {
	symbol: '{{unsubscribe}}',
	text: 'Unsubscribe Footer',
	value: 'email.unsubscribeFooter',
};

export const FileRequestLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{fileRequest}}', '{{fileRequestLink}}'],
	symbol: '{{file request}}',
	text: 'Securely Upload Files',
	value: 'survey.fileRequestLink',
};

export const FileSendLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: [
		'{{sendfiles}}',
		'{{download files}}',
		'{{downloadfiles}}',
		'{{file download}}',
		'{{filedownload}}',
	],
	symbol: '{{send files}}',
	text: 'Securely Download Files',
	value: 'fileDownloadLink',
};

export const FullNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{fullName}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{full name}}',
	text: 'full name',
	value: 'contact.fullName',
};

export const MeetingHomepageLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting homepage link}}', '{{meetingHomepageLink}}', '{{meetingHomepage}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meeting homepage}}',
	text: 'Meeting Homepage Link',
	value: 'meeting.homepageLink',
};

export const MeetingRescheduleLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting reschedule link}}', '{{reschedule link}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meeting reschedule}}',
	text: 'Meeting Reschedule Link',
	value: 'meeting.rescheduleLink',
};

export const MeetingCancelLinkPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting cancel link}}', '{{cancel link}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meeting cancel}}',
	text: 'Meeting Cancel Link',
	value: 'meeting.cancelLink',
};

export const RenewalKeyFactPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{policies}}', '{{renewing policies}}'],
	symbol: '{{renewalKeyFact}}',
	text: 'Renewing Policies',
	value: 'keyfact.renewalKeyFact',
};

export const MeetingDatePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting date}}'],
	symbol: '{{meetingDate}}',
	text: 'meeting date',
	value: 'meeting.meetingDate',
};

export const MeetingDayPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting day}}'],
	symbol: '{{meetingDay}}',
	text: 'meeting day',
	value: 'meeting.meetingDay',
};

export const MeetingTimePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting time}}'],
	symbol: '{{meetingTime}}',
	text: 'meeting time',
	value: 'meeting.meetingTime',
};

export const MeetingTimeZonePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting timezone}}', '{{meeting time zone}}'],
	symbol: '{{meetingTimeZone}}',
	text: 'meeting timezone',
	value: 'meeting.meetingTimeZone',
};

export const MeetingVirtualLocation: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{virtual meeting link}}'],
	symbol: '{{virtualMeetingLink}}',
	text: 'virtual meeting link',
	value: 'meeting.virtualMeetingLink',
};

export const MeetingLocationPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting location}}'],
	symbol: '{{meetingLocation}}',
	text: 'meeting location',
	value: 'meeting.location',
};

export const DonationsSummaryPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{donation summary}}'],
	symbol: '{{donations}}',
	text: 'donations',
	value: 'donations',
};

export const PoliciesPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{policy summary}}'],
	symbol: '{{new policies}}',
	text: 'New Policies',
	value: 'policy.policies',
};

export const AllPoliciesPlaceholder: IRichContentDocumentEditorPlaceholder = {
	symbol: '{{all policies}}',
	text: 'All Policies',
	value: 'policy.allPolicies',
};

export const PoliciesExpiringPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{expiring policies}}'],
	symbol: '{{policies expiring}}',
	text: 'Policies Expiring',
	value: 'policy.policiesExpiring',
};

export const MattersPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{matter list}}', '{{matterlist}}', '{{new matters}}'],
	symbol: '{{matters}}',
	text: 'Matters',
	value: 'matter.matters',
};

export const ServiceJobsPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{job list}}', '{{joblist}}', '{{new service jobs}}'],
	symbol: '{{service jobs}}',
	text: 'Service Jobs',
	value: 'serviceJob.serviceJobs',
};

// Policy Fields for New Policy Automation
export const PolicyNumberPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{policyNumber}}', '{{policy number}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.policyNumber}}',
	text: 'policy number',
	value: 'policy.policyNumber',
};

export const LineOfBusinessPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{lineOfBusiness}}', '{{line of business}}', '{{lob}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.lineOfBusiness}}',
	text: 'line of business',
	value: 'policy.lineOfBusiness',
};

export const PolicyCarrierPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{carrier}}', '{{insurance carrier}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.carrier}}',
	text: 'carrier',
	value: 'policy.carrier',
};

export const PolicyEffectiveDatePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{effectiveDate}}', '{{effective date}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.effectiveDate}}',
	text: 'effective date',
	value: 'policy.effectiveDate',
};

export const PolicyRenewalDatePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{policy renewalDate}}', '{{policy renewal date}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.renewalDate}}',
	text: 'policy renewal date',
	value: 'policy.renewalDate',
};

export const PolicyPremiumPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{premium}}', '{{policy premium}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{policy.premium}}',
	text: 'premium',
	value: 'policy.premium',
};

// Meeting Booking Information Fields
export const MeetingFirstNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting first name}}', '{{meetingfirstname}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingFirstName}}',
	text: 'meeting first name',
	value: 'meeting.firstName',
};

export const MeetingLastNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting last name}}', '{{meetinglastname}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingLastName}}',
	text: 'meeting last name',
	value: 'meeting.lastName',
};

export const MeetingPhoneNumberPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting phone number}}', '{{meetingphonenumber}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingPhoneNumber}}',
	text: 'meeting phone number',
	value: '{{meeting phone number}}',
};

export const MeetingCompanyNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting company name}}', '{{meetingcompanyname}}', '{{meeting company}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingCompanyName}}',
	text: 'meeting company name',
	value: 'meeting.companyName',
};

export const MeetingEmailAddressPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting email address}}', '{{meetingemailaddress}}', '{{meeting email}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingEmailAddress}}',
	text: 'meeting email address',
	value: 'meeting.emailAddress',
};

export const MeetingCommentsPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting comments}}', '{{meetingcomments}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingComments}}',
	text: 'meeting comments',
	value: 'meeting.comments',
};

export const MeetingDisplayNamePlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{meeting display name}}', '{{meetingdisplayname}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{meetingDisplayName}}',
	text: 'meeting display name',
	value: 'meeting.displayName',
};

// Additional Contact Information Fields for Meetings
export const EmailAddressPlaceholder: IRichContentDocumentEditorPlaceholder = {
	additionalSymbols: ['{{emailaddress}}'],
	onClickCommand: levPlaceholderPopoverCommand,
	onCloseCommand: levPlaceholderPopoverCloseCommand,
	symbol: '{{email address}}',
	text: 'email address',
	value: 'contact.emailAddress',
};

export const DefaultContactPlaceholders = [
	FirstNamePlaceholder,
	LastNamePlaceholder,
	CompanyNamePlaceholder,
	JobTitlePlaceholder,
	AddressPlaceholder,
	Address2Placeholder,
	CityPlaceholder,
	StatePlaceholder,
	ZipCodePlaceholder,
	CountryPlaceholder,
	FullAddressPlaceholder,
	BioPlaceholder,
	FullNamePlaceholder,
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingTimePlaceholder,
	MeetingVirtualLocation,
	MeetingLocationPlaceholder,
];

const DefaultEmailPlaceholders = [
	...DefaultContactPlaceholders,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderEmailPlaceholder,
	SenderPhonePlaceholder,
	RenewalKeyFactPlaceholder,
	DonationsSummaryPlaceholder,
	// Policy fields - available in all email flows
	PoliciesPlaceholder,
	AllPoliciesPlaceholder,
	PoliciesExpiringPlaceholder,
	PolicyNumberPlaceholder,
	LineOfBusinessPlaceholder,
	PolicyCarrierPlaceholder,
	PolicyEffectiveDatePlaceholder,
	PolicyRenewalDatePlaceholder,
	PolicyPremiumPlaceholder,
	// Matter fields - available in all email flows
	MattersPlaceholder,
	// Service job fields - available in all email flows
	ServiceJobsPlaceholder,
	FileRequestLinkPlaceholder,
	FileSendLinkPlaceholder,
];

export const NewPolicyEmailPlaceholders = [
	PoliciesPlaceholder,
	...DefaultEmailPlaceholders,
	PolicyNumberPlaceholder,
	LineOfBusinessPlaceholder,
	PolicyCarrierPlaceholder,
	PolicyEffectiveDatePlaceholder,
	PolicyRenewalDatePlaceholder,
	PolicyPremiumPlaceholder,
];

export const DefaultMeetingEmailPlaceholders = [
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingTimePlaceholder,
	MeetingTimeZonePlaceholder,
];

export const CompleteMeetingPlaceholders = [
	// Contact Information Fields
	FirstNamePlaceholder,
	LastNamePlaceholder,
	FullNamePlaceholder,
	EmailAddressPlaceholder,
	CompanyNamePlaceholder,
	JobTitlePlaceholder,
	AddressPlaceholder,
	Address2Placeholder,
	CityPlaceholder,
	StatePlaceholder,
	ZipCodePlaceholder,
	CountryPlaceholder,
	FullAddressPlaceholder,
	BioPlaceholder,

	// Meeting Booking Details
	MeetingFirstNamePlaceholder,
	MeetingLastNamePlaceholder,
	MeetingPhoneNumberPlaceholder,
	MeetingCompanyNamePlaceholder,
	MeetingEmailAddressPlaceholder,
	MeetingCommentsPlaceholder,
	MeetingDisplayNamePlaceholder,

	// Meeting Schedule Fields
	MeetingDayPlaceholder,
	MeetingDatePlaceholder,
	MeetingTimePlaceholder,
	MeetingTimeZonePlaceholder,
	MeetingVirtualLocation,
	MeetingLocationPlaceholder,

	// Action Links
	MeetingRescheduleLinkPlaceholder,
	MeetingCancelLinkPlaceholder,
	MeetingHomepageLinkPlaceholder,

	// Sender Information
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderEmailPlaceholder,
	SenderPhonePlaceholder,
];

export const GetDefaultEmailPlaceholders = (
	userSession: UserSessionContext,
	impersonationContext?: IImpersonationContext,
	_resourceSelector?: ResourceSelectorId
): IRichContentDocumentEditorPlaceholder[] => {
	const account = impersonationContext?.account || userSession?.account;

	// The file request link resolves to a survey-backed upload link, so it should only be offered
	// when the Files feature is available (Surveys + Files enabled). When it's off, drop it from
	// the recognized placeholders so it isn't styled/offered.
	const fileRequestEnabled = isFilesFeatureEnabled(account);
	const canSendFileLinks = fileRequestEnabled && !!account?.features?.files?.canSendFileLinks;
	const placeholders = DefaultEmailPlaceholders.filter(placeholder => {
		if (placeholder === FileRequestLinkPlaceholder) {
			return fileRequestEnabled;
		}
		if (placeholder === FileSendLinkPlaceholder) {
			return canSendFileLinks;
		}
		return true;
	});

	if (account?.features?.meetingScheduler?.enabled) {
		placeholders.push(MeetingHomepageLinkPlaceholder);
	}

	return placeholders;
};

/**
 * Checks if a merge field match is within a URL context (href attribute, http(s) URLs, etc.)
 * to determine if it should be exempt from span wrapping
 */
const isWithinUrlContext = (content: string, matchIndex: number): boolean => {
	// Get a reasonable context window around the match
	const contextStart = Math.max(0, matchIndex - 200);
	const contextEnd = Math.min(content.length, matchIndex + 200);
	const contextBefore = content.substring(contextStart, matchIndex);
	const contextAfter = content.substring(matchIndex, contextEnd);

	// Check if we're inside an href attribute
	const hrefPattern = /href\s*=\s*["'][^"']*$/i;
	const hrefEndPattern = /^[^"']*["']/;
	if (hrefPattern.test(contextBefore) && hrefEndPattern.test(contextAfter)) {
		return true;
	}

	// Check if we're in the middle of a URL (http/https)
	const urlPattern = /https?:\/\/[^\s<>"']*$/i;
	const urlEndPattern = /^[^\s<>"']*/;
	if (urlPattern.test(contextBefore) && urlEndPattern.test(contextAfter)) {
		return true;
	}

	// Check if we're inside a src attribute
	const srcPattern = /src\s*=\s*["'][^"']*$/i;
	const srcEndPattern = /^[^"']*["']/;
	if (srcPattern.test(contextBefore) && srcEndPattern.test(contextAfter)) {
		return true;
	}

	return false;
};

/**
 * Helper method for organizing placeholders by their value
 *
 * @param types Placeholders
 */
const placeholdersByValue = (types: IRichContentDocumentEditorPlaceholder[]) => {
	return types.reduce<IDictionary<IRichContentDocumentEditorPlaceholder>>((dictionary, x) => {
		if (x.value) {
			dictionary[x.value] = x;
		}
		return dictionary;
	}, {});
};

const getSymbolsRegExpFormat = (symbols: string[]) => {
	return symbols.reduce<string>((regexpFormat, x, i, collection) => {
		const prefix = `${i === collection.length - 1 ? '(' : ''}`;
		const suffix = `${i === collection.length - 1 ? ')' : ''}`;
		return `${prefix}${regexpFormat}${i > 0 ? '|' : ''}${escapeRegExp(x)}${suffix}`;
	}, '');
};

export class Token {
	private html: Element;
	private type: IRichContentDocumentEditorPlaceholder;

	public static replaceImpostorPlaceholders = (
		content: string,
		placeholders: IRichContentDocumentEditorPlaceholder[]
	) => {
		let newContent = content;
		const placeholderTexts = placeholders
			.map(placeholder => placeholder.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
			.join('|');

		/*
		// Match styled impostor placeholder
		const placeholderImposterRegex =
		new RegExp(`<span[^>]*\\s+style=(["'])(?:(?!\\1).)*border\\s*:\\s*(?=[^;]*dashed)(?=[^;]*(?:#00AAE8|rgb\\(0,\\s*170,\\s*232\\)))
		(?=[^;]*1(?:\\.[0-9]+)?(?:p[tx]))(?:(?!\\1).)*\\1[^>]*>\\s*(${placeholderTexts})\\s*</\\s*span>`, 'igm'); */
		const placeholderImposterRegex = new RegExp(`<span[^>]*>\\s*(${placeholderTexts})\\s*</\\s*span>`, 'igm');
		for (;;) {
			const impostorMatch = placeholderImposterRegex.exec(content);
			if (impostorMatch == null) {
				break;
			}
			const impostorMatchedString = impostorMatch[0]; // <span style=\"border: dashed #00AAE8 1.0pt; padding: 1.0pt;\">first name</span>
			const placeholderText = impostorMatch[1]; // first name|last name|...
			const placeholder = placeholders.find(p => p.text === placeholderText);
			if (!placeholder) {
				continue;
			}
			const token = new Token(placeholder);
			newContent = newContent.replace(impostorMatchedString, token.toHTMLString());
		}
		newContent = newContent.replace(
			/\s?border:\s((?=[^;'"]*dashed)(?=[^;'"]*1(?:\.[0-9]+)?p[tx])(?=[^;'"]*#00aae8)[^;'"]*)/gim,
			''
		);
		return newContent;
	};

	public static getPlaceholderHtmlStringValue = (type: IRichContentDocumentEditorPlaceholder) => {
		return `<span ${TokenAttributes.ContentEditable}="false" ${TokenAttributes.DataId}="${uuid()}" ${
			TokenAttributes.DataPlaceholder
		}="true" ${TokenAttributes.DataPlaceholderValue}="${type.value}">${type.text}</span>`;
	};

	/**
	 * Try/catch this function to show an error if there is a broken token.
	 *
	 * @param content Raw HTML as a string
	 * @param type Placeholder type.. like FirstNamePlaceholder
	 */
	public static validateContent = (content: string, types: IRichContentDocumentEditorPlaceholder[]) => {
		const doc = new DOMParser().parseFromString(content, 'text/html');
		const spans = doc.querySelectorAll('span[data-placeholder]');
		spans.forEach(span => {
			const dataPlaceholderValue = span.getAttribute('data-placeholder-value');
			const matchingType = dataPlaceholderValue ? types.find(x => x.value === dataPlaceholderValue) : null;
			if (matchingType) {
				const token = new Token(matchingType, span.outerHTML);
				if (!token.isValid()) {
					throw new Error(
						`There appears to be an invalid token "${span?.innerHTML}" in this editor. Please remove it and type "${matchingType.symbol}" to automatically replace it with the correct one.`
					);
				}
			}
		});
	};

	/**
	 * Try/catch this function to show an error if there is a broken token.
	 *
	 * @param content Raw HTML as a string
	 * @param type Placeholder type.. like FirstNamePlaceholder
	 * @param replace Symbol to replace.. optional override
	 */
	public static replaceContent = (content: string, type: IRichContentDocumentEditorPlaceholder, replace?: string) => {
		let newContent = content;
		const addedTokens: Token[] = [];
		if (!content) {
			return ['', addedTokens] as const;
		}

		Token.validateContent(content, [type]);

		const stringToReplace = replace ?? getSymbolsRegExpFormat([type.symbol, ...(type.additionalSymbols || [])]);
		const replacementRegExp = new RegExp(stringToReplace, 'gim');

		let replacementMatch: RegExpExecArray = null;
		do {
			replacementMatch = replacementRegExp.exec(newContent);
			if (replacementMatch) {
				try {
					// Check if this merge field is within a URL context
					const matchIndex = replacementMatch.index;
					if (isWithinUrlContext(newContent, matchIndex)) {
						// Skip wrapping with span for merge fields in URLs
						continue;
					}

					// replace symbol with actual token
					const token = new Token(type);
					newContent = newContent.replace(new RegExp(stringToReplace, 'im'), token.toHTMLString());
					addedTokens.push(token);
				} catch {
					// swallow this (shouldn't happen, but to prevent white screen just in case)
				}
			}
		} while (replacementMatch !== null);
		return [newContent, addedTokens] as const;
	};

	/**
	 * Try/catch this function to show an error if there is a broken token.
	 *
	 * @param content Raw HTML as a string
	 * @param type Placeholder type.. like FirstNamePlaceholder
	 * @param replace String to replace token with
	 */
	public static replaceToken = (content: string, type: IRichContentDocumentEditorPlaceholder, replace: string) => {
		let newContent = content;
		if (!content) {
			return '';
		}

		// Replace string token
		const replacementStringRegExp = new RegExp(type.symbol, 'gim');

		let replacementStringMatch: RegExpExecArray = null;
		do {
			replacementStringMatch = replacementStringRegExp.exec(newContent);
			if (replacementStringMatch) {
				try {
					// replace symbol with string
					newContent = newContent.replace(type.symbol, replace);
				} catch {
					// swallow this (shouldn't happen, but to prevent white screen just in case)
				}
			}
		} while (replacementStringMatch !== null);

		// Replace HTML token
		const token = new Token(type, newContent);
		if (token.isValid()) {
			const replacementHtmlRegExp = new RegExp(escapeRegExp(token.toHTMLString()), 'gim');

			let replacementHtmlMatch: RegExpExecArray = null;
			do {
				replacementHtmlMatch = replacementHtmlRegExp.exec(newContent);
				if (replacementHtmlMatch) {
					try {
						// replace symbol with string
						newContent = newContent.replace(token.toHTMLString(), replace);
					} catch {
						// swallow this (shouldn't happen, but to prevent white screen just in case)
					}
				}
			} while (replacementHtmlMatch !== null);
		}

		return newContent;
	};

	/**
	 * Will return valid token(s) in an html string Try/catch this function to show an error if there is a broken token.
	 *
	 * @param htmlStringValue Raw HTML as a string
	 * @param types Placeholder types to search for
	 */
	public static cleanHtmlString = (htmlStringValue: string, types: IRichContentDocumentEditorPlaceholder[]) => {
		const doc = new DOMParser().parseFromString(htmlStringValue, 'text/html');
		const valueToType = placeholdersByValue(types);
		doc.querySelectorAll('span[data-placeholder]').forEach(el => {
			const value = el.getAttribute(TokenAttributes.DataPlaceholderValue);

			const type = valueToType[value];
			if (type) {
				const token = new Token(type, el.outerHTML).makeValid(type).removeKnownInlineStyles();

				el.replaceWith(token.toHTMLElement());
			}
		});

		return doc.body?.innerHTML;
	};

	/**
	 * @param types Types to search for
	 * @param htmlStringValue Html to search over
	 * @returns Parsed tokens
	 */
	public static fromHtml = (types: IRichContentDocumentEditorPlaceholder[], htmlStringValue?: string) => {
		const tokens: Token[] = [];
		if (htmlStringValue) {
			const doc = new DOMParser().parseFromString(htmlStringValue, 'text/html');
			const valueToType = placeholdersByValue(types);
			doc.querySelectorAll('span[data-placeholder]').forEach(el => {
				const value = el.getAttribute(TokenAttributes.DataPlaceholderValue);

				const type = valueToType[value];
				if (type) {
					tokens.push(new Token(type, el.outerHTML));
				}
			});
		}

		return tokens;
	};

	public static isFirstNameTokenFormattedCorrectlyStrict = (content: IRawRichTextContentState): boolean => {
		const { document } = content;

		if (!content || !document.includes('first name')) {
			return true;
		}

		const correct = Token.isFirstNameTokenFormattedCorrectlyLoose(content);
		if (!correct) {
			return correct;
		}

		const token = new Token(FirstNamePlaceholder, document);
		return token.isValid();
	};

	public static isFirstNameTokenFormattedCorrectlyLoose = (content: IRawRichTextContentState): boolean => {
		const { document } = content;

		if (!content || !document.includes('first name')) {
			return true;
		}

		// Leave this in for now, but the goal would be to take it out eventually.
		const stringRegex = /{{first name}}/;

		if (stringRegex.test(document)) {
			return true;
		}

		// first name appears to be used in the middle of a sentence

		if (/first name\s/i.test(document) || /\sfirst name/i.test(document)) {
			return true;
		}

		// token is missing brackets
		if (
			/\s{{first name}[\s,]/.test(document) ||
			/\s{first name}}[\s,]/.test(document) ||
			/\s{first name}[\s,]/.test(document)
		) {
			return false;
		}

		return true;
	};

	constructor(type: IRichContentDocumentEditorPlaceholder, html?: string) {
		const value =
			html ??
			`<span contenteditable="false" data-id="${uuid()}" data-placeholder="true" data-placeholder-value="${
				type.value
			}">${type.text}</span>`;
		const doc = new DOMParser().parseFromString(value, 'text/html');
		const span = doc.querySelector('span[data-placeholder]');

		this.html = span;
		this.type = type;

		return this;
	}

	public contentEditable = (to: ContentEditableOptions) => {
		this.html.setAttribute(TokenAttributes.ContentEditable, to);
		return new Token(this.type, this.html.outerHTML);
	};

	public dataPlaceholder = (to: DataPlaceholderOptions) => {
		this.html.setAttribute(TokenAttributes.DataPlaceholder, to);
		return new Token(this.type, this.html.outerHTML);
	};

	public dataPlaceholderValue = (to: string) => {
		this.html.setAttribute(TokenAttributes.DataPlaceholderValue, to);
		return new Token(this.type, this.html.outerHTML);
	};

	public dataId = (to: string) => {
		this.html.setAttribute(TokenAttributes.DataId, to);
		return new Token(this.type, this.html.outerHTML);
	};

	public getAttribute = (attribute: string) => {
		return this.html.getAttribute(attribute);
	};

	public removeKnownInlineStyles = () => {
		const span = this.html as HTMLSpanElement;

		if (span.style?.background === 'rgb(243, 243, 243)' || span.style?.background.toLowerCase() === '#f3f3f3') {
			span.style.background = '';
		}

		if (span.style?.borderRadius === '3px') {
			span.style.borderRadius = '';
		}

		if (
			span.style?.border === '1px dashed rgb(0, 170, 232)' ||
			span.style?.border.toLowerCase() === '1px dashed #00aae8'
		) {
			span.style.border = '';
		}

		if (span.style?.boxSizing === 'border-box') {
			span.style.boxSizing = '';
		}

		if (span.style?.padding === '1px 5px') {
			span.style.padding = '';
		}

		return new Token(this.type, span.outerHTML);
	};

	public isValid = () => {
		// const contentEditable = this.html?.getAttribute(TokenAttributes.ContentEditable) === ContentEditableOptions.False;
		const dataPlaceholder = this.html?.getAttribute(TokenAttributes.DataPlaceholder) === DataPlaceholderOptions.True;
		const dataPlaceholderValue = this.html?.getAttribute(TokenAttributes.DataPlaceholderValue) === this.type.value;

		// return contentEditable && dataPlaceholder && dataPlaceholderValue;
		return dataPlaceholder && dataPlaceholderValue;
	};

	public makeValid = (type: IRichContentDocumentEditorPlaceholder = this.type) => {
		return this.contentEditable(ContentEditableOptions.False)
			.dataId(this.getAttribute(TokenAttributes.DataId) || uuid())
			.dataPlaceholder(DataPlaceholderOptions.True)
			.dataPlaceholderValue(type.value);
	};

	public toHTMLString = () => {
		return this.html.outerHTML;
	};

	public toHTMLElement = () => {
		return this.html as HTMLSpanElement;
	};
}
